import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "../locales/en";
import hi from "../locales/hi";
import { PHRASES, PATTERNS } from "../locales/hi_phrases";

const DICTS = { en, hi };
const get = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
const missing = new Set();
const HI = { ...hi.forms, ...PHRASES };
en.faq.forEach(([q, a], i) => { HI[q] = hi.faq[i][0]; HI[a] = hi.faq[i][1]; });
let current = localStorage.getItem("lang") === "hi" ? "hi" : "en";

const miss = (k) => {
  if (missing.has(k)) return;
  missing.add(k);
  console.warn(`[i18n] missing ${k}`);
  window.__i18nMissing = [...missing];
};
window.__i18nMissing = [];

export function tr(s, lang = current) {
  if (lang !== "hi" || typeof s !== "string" || !/[A-Za-z]/.test(s) || /[\u0900-\u097F]/.test(s)) return s;
  const v = HI[s] ?? HI[s.trim()];
  if (v !== undefined) return v;
  for (const [re, fn] of PATTERNS) {
    const m = s.match(re);
    if (m) return fn(m, (x) => tr(x, lang));
  }
  miss(`hi phrase: ${s}`);
  return s;
}
export const tc = (code, lang = current) => tr(String(code ?? "—").replace(/_/g, " "), lang);
export const getLang = () => current;

const Ctx = createContext({ lang: "en", setLang: () => {}, t: (k) => k, tl: (s) => s, tc: (s) => s });

export function LangProvider({ children }) {
  const [lang, setLang] = useState(current);
  current = lang;
  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang === "hi" ? "hi" : "en-IN";
  }, [lang]);
  const t = useCallback((key, vars, fallback) => {
    let v = get(DICTS[lang], key);
    if (v === undefined && fallback !== undefined) return lang === "hi" ? tr(fallback, lang) : fallback;
    if (v === undefined) {
      miss(`${lang} key: ${key}`);
      v = get(en, key) ?? key;
    }
    if (typeof v === "string" && vars) v = v.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");
    return v;
  }, [lang]);
  const tl = useCallback((s) => tr(s, lang), [lang]);
  const tcode = useCallback((s) => tc(s, lang), [lang]);
  const value = useMemo(() => ({ lang, setLang, t, tl, tc: tcode }), [lang, t, tl, tcode]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useT = () => useContext(Ctx);

export function LangSwitch({ className = "" }) {
  const { lang, setLang, t } = useT();
  return (
    <div role="group" aria-label={t("nav.language")} className={`inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm shrink-0 ${className}`} data-testid="lang-switch">
      {[["en", "English"], ["hi", "हिंदी"]].map(([k, l]) => (
        <button key={k} type="button" lang={k} onClick={() => setLang(k)} aria-pressed={lang === k}
          className={`px-3 py-1.5 font-semibold transition-colors ${lang === k ? "bg-[#0B1B3D] text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`} data-testid={`lang-${k}`}>{l}</button>
      ))}
      <span className="sr-only" aria-live="polite">{lang === "hi" ? "हिंदी चुनी गई" : "English selected"}</span>
    </div>
  );
}
